import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { GameCenterFormTypes } from "configs/types";
import { useAppSelector, useAppDispatch } from "store/index";
import { handleUpdateFields } from "store/gameCenter/tags/slice";
import { handleUpdateFields as handleUpdateFieldsCollection } from "store/gameCenter/collections/slice";
import {
  handleUpdateFields as handleUpdateFieldsCategories,
  CategoryItem,
} from "store/gameCenter/categories/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Button from "components/buttons/Button";

import cn from "classnames";
import { ImageUpload } from "components/imageUpload";

type Props = {
  type: GameCenterFormTypes;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  isReadOnly?: boolean;
};

const GameCenterCommonMainTab: React.FC<Props> = ({
  type,
  fieldErrors,
  clearFieldError,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const payload = useAppSelector((store) => {
    if (type === GameCenterFormTypes.collection) {
      return store.collections.payload;
    }
    if (type === GameCenterFormTypes.category) {
      return store.categories.payload;
    }
    return store.tags.payload;
  });

  const [imgPreview, setImgPreview] = useState<string>("");
  const [options, setOptions] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [selectedItem, setSelectedItem] = useState<number>();
  const [description, setDescription] = useState<string>("");
  const [isDescriptionFetching, setIsDescriptionFetching] =
    useState<boolean>(false);

  useEffect(() => {
    // @ts-ignore
    if (type === GameCenterFormTypes.collection && payload?.categoryId) {
      // @ts-ignore
      setSelectedItem(payload?.categoryId);
    }
  }, [type, payload]);

  const handleCategories = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.categoriesList}?limit=500&offset=0`,
      );
      const array = result.data.items.map((el: CategoryItem) => {
        return {
          value: el.id,
          label: el.title,
        };
      });
      setOptions(array);
    } catch (error) {
      console.log(error, "error");
    }
  };
  useEffect(() => {
    if (type === GameCenterFormTypes.collection) {
      handleCategories();
    }
  }, [type]);

  useEffect(() => {
    if (payload?.image) {
      if (typeof payload.image === "string") {
        setImgPreview(payload.image);
      } else {
        const file = payload?.image;
        if (file && file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setImgPreview(reader.result as string);
          };
          reader.readAsDataURL(file);
        }
      }
    }
  }, [payload]);

  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    switch (type) {
      case GameCenterFormTypes.tag:
        dispatch(handleUpdateFields({ [key]: img ?? value }));
        break;
      case GameCenterFormTypes.collection:
        dispatch(handleUpdateFieldsCollection({ [key]: img ?? value }));
        break;
      case GameCenterFormTypes.category:
        dispatch(handleUpdateFieldsCategories({ [key]: img ?? value }));
        break;

      default:
        break;
    }
  };

  const handleAIText = async () => {
    setIsDescriptionFetching(true);
    try {
      const result = await instance.post(
        payload?.id ? apiRoutes.generateAIText : apiRoutes.createAIText,
        {
          instanceType: type,
          attribute: "description",
          instanceId: payload?.id ?? -1,
          fromField: payload?.id ? undefined : payload?.title,
        },
      );
      if (!description) {
        setDescription(payload?.description ?? "empty");
      }
      handleData("description", result.data?.text ?? "");
    } catch {
      console.log("handleAIText error");
    }
    setIsDescriptionFetching(false);
  };

  return (
    <div className="game-form-wrapper">
      <div className="game-form-content">
        <div className="game-form-box">
          {type === GameCenterFormTypes.collection && (
            <div className="game-form-box--item">
              <DefaultDropdown
                value={selectedItem}
                placeholder={t("selectCategory")}
                label={t("category")}
                options={options}
                disabled={isReadOnly || options.length === 0}
                onSelect={(e) => {
                  setSelectedItem(e as number);
                  handleData("categoryId", e);
                  clearFieldError("categoryId");
                }}
                hasError={Boolean(fieldErrors?.["categoryId"])}
              />
            </div>
          )}
          <div className="game-form-box--item">
            <InnerInput
              value={payload?.title ?? ""}
              placeholder={t("enterTitle")}
              label={t("title")}
              type="text"
              id="game-title"
              disabled={isReadOnly}
              onChange={(e) => {
                handleData("title", e);
                clearFieldError("title");
              }}
              hasError={Boolean(fieldErrors?.["title"])}
            />
          </div>
          <div className="game-form-box--item">
            <InnerInput
              value={payload?.slug ?? ""}
              placeholder={t("enterSlug")}
              label={t("slug")}
              type="text"
              id="game-slug"
              disabled={isReadOnly}
              onChange={(e) => {
                handleData("slug", e);
                clearFieldError("slug");
              }}
              hasError={Boolean(fieldErrors?.["slug"])}
            />
          </div>
          <div className="game-form-box--item with-ai">
            <div className="ai-buttons">
              <Button
                handleClick={() => {
                  handleData(
                    "description",
                    description === "empty" ? "" : description,
                  );
                  setDescription("");
                }}
                disabled={isReadOnly || !description || isDescriptionFetching}
                isSecondary
                txt="&larr;"
              />
              <Button
                disabled={isReadOnly || isDescriptionFetching || !payload?.title}
                handleClick={handleAIText}
                txt={t("aiAssist")}
              />
            </div>
            <InnerInput
              maxCodePoints={2500}
              value={payload?.description ?? ""}
              placeholder={t("enterText")}
              label={t("description")}
              type="textarea"
              id="game-description"
              disabled={isReadOnly || isDescriptionFetching}
              onChange={(e) => {
                handleData("description", e);
                clearFieldError("description");
              }}
              hasError={Boolean(fieldErrors?.["description"])}
            />
          </div>
        </div>
        <div className="game-form-box">
          <div className="game-form-img game-form-img--game center">
            <div className="game-form-field">
              <div className="game-form-img-label">Image</div>

              <div
                className={cn("game-item", {
                  "has-error": fieldErrors?.["file"],
                })}
              >
                <ImageUpload
                  image={payload?.image}
                  isActive={Boolean(payload?.isActive)}
                  size="large"
                  disabled={isReadOnly}
                  onAddImage={(file) => {
                    handleData("image", "", file);
                    clearFieldError("file");
                  }}
                />
              </div>
            </div>
            <div className="game-form-controls">
              <div className="game-form-switcher">
                <SwitchBtn
                  activeTxt={t("active")}
                  notActiveTxt={t("hidden")}
                  isActive={!!payload?.isActive}
                  disabled={isReadOnly}
                  handleClick={() => {
                    handleData(
                      "isActive",
                      payload?.isActive ? !payload?.isActive : true,
                    );
                    clearFieldError("isActive");
                  }}
                  hasError={Boolean(fieldErrors?.["isActive"])}
                />
              </div>
              <div className="game-form-icon">
                <div className="game-form-img-label">Icon</div>
                <ImageUpload
                  image={payload?.icon}
                  isActive={Boolean(payload?.isActive)}
                  disabled={isReadOnly}
                  onAddImage={(file) => {
                    handleData("icon", "", file);
                  }}
                  type="square"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameCenterCommonMainTab;
